import { useEffect, useState } from "react";

import { Box, Skeleton, Tooltip, Typography } from "@mui/material";

import { parseFocusRectFromAwsJson } from "../../../../../../../common/helpers/focusUtils";
import { GoToPTHandlerType } from "../../../../../../../common/hooks/usePTController";
import { selectedKeyframeVariable } from "../../../../../../../common/variables/selectedKeyframe";
import S3Image from "./../../../../../../../common/components/media/S3Image";
import { v4 as uuid } from "uuid";
import { IZonesByDeviceTableRow } from "./hooks/usePTGetZonesByDeviceTableRow";
import {
  firstZoneVariable,
  selectedZoneVariable,
  useSelectedZoneVariable,
} from "./zones.variable";

interface IZoneListTableProps {
  rows: IZonesByDeviceTableRow[];
  loading: boolean;
  hasZoom?: boolean;
  disableRowClick?: boolean;
  handleGoToPt?: GoToPTHandlerType;
}

const PTZoneListTable = ({
  rows,
  loading,
  hasZoom,
  disableRowClick = false,
  handleGoToPt,
}: IZoneListTableProps): JSX.Element => {
  const selectedZone = useSelectedZoneVariable();
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);

  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    const row = rows[0];

    if (selectedZone.pan === row.pan && selectedZone.tilt === row.tilt) {
      return;
    }

    selectedKeyframeVariable(row?.signedUrlKeyFrame ?? "");

    const firstZone = {
      zoneName: row?.name,
      pan: row?.pan,
      tilt: row?.tilt,
      zoom: row?.zoom,
    };

    firstZoneVariable(firstZone);
  }, [rows.length, selectedZone]);

  const handleRowClick = (params: IZonesByDeviceTableRow): void => {
    if (disableRowClick) {
      return;
    }

    selectedKeyframeVariable(params?.signedUrlKeyFrame ?? "");

    selectedZoneVariable({
      zoneName: params?.name,
      pan: params?.pan,
      tilt: params?.tilt,
      zoom: params?.zoom,
    });

    if (handleGoToPt) {
      const parsedFocusRect =
        params.focusMode === "auto"
          ? parseFocusRectFromAwsJson(params.focusRect)
          : null;

      handleGoToPt(
        params.pan,
        params.tilt,
        params.zoom,
        params.name,
        false,
        parsedFocusRect,
        params.focusDistance
      );
    }
  };

  return (
    <Box>
      <Box>
        {rows.map((row, index): JSX.Element | null => {
          if (!row.status) {
            return null;
          }

          const isLastRow = index === rows.length - 1;
          const isActive =
            selectedZone.pan === row.pan &&
            selectedZone.tilt === row.tilt &&
            selectedZone.zoom === row.zoom;

          return (
            <Box
              key={row.id}
              sx={{
                display: "flex",
                gap: "1em",
                padding: "1em",
                borderBottom: isLastRow ? "none" : "1px solid",
                borderColor: "divider",
                alignItems: "stretch",
              }}
            >
              <Box>
                <Tooltip
                  key={row.id}
                  title={
                    disableRowClick ? "Camera is managed by the model" : ""
                  }
                  disableHoverListener={!!disableRowClick}
                  disableFocusListener={!!disableRowClick}
                  disableTouchListener={!!disableRowClick}
                  open={
                    disableRowClick
                      ? openTooltipId === String(row.id)
                      : undefined
                  }
                >
                  <Box
                    sx={{
                      borderRadius: "8px",
                      boxSizing: "border-box",
                      border: "2px solid transparent",
                      cursor: disableRowClick ? "not-allowed" : "pointer",

                      "&:hover": {
                        borderColor: disableRowClick
                          ? "transparent"
                          : "primary.main",
                      },

                      ...(isActive && {
                        borderColor: "primary.main",
                      }),
                    }}
                    onClick={(): void => {
                      if (disableRowClick) {
                        const idStr = String(row.id);

                        setOpenTooltipId(prev =>
                          prev === idStr ? null : idStr
                        );

                        return;
                      }

                      handleRowClick(row);
                    }}
                    onMouseLeave={(): void => {
                      if (disableRowClick && openTooltipId === String(row.id)) {
                        setOpenTooltipId(null);
                      }
                    }}
                  >
                    <S3Image
                      s3Key={row.signedUrlKeyFrame ?? ""}
                      mediaStatusSx={{
                        "& .MuiTypography-root": {
                          fontSize: "50%",
                        },
                        width: "96px",
                        height: "60px",
                        overflow: "hidden",
                        borderRadius: "6px",
                      }}
                      fixedHeight="60px"
                      sx={{
                        borderRadius: "6px",
                        objectFit: "cover",
                        width: "96px",
                        height: "60px",
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.65em",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5em",
                  }}
                >
                  <Typography
                    sx={{
                      color: isActive ? "primary.dark" : "text.primary",
                    }}
                    variant="buttonMedium"
                  >
                    {row.name}
                  </Typography>

                  {isActive && (
                    <Typography
                      variant="tag"
                      sx={{
                        color: "primary.dark",
                      }}
                    >
                      (Active)
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="inputLabel">Pan:</Typography>
                  <Typography variant="body2Regular">{row.pan}</Typography>
                  <Typography variant="inputLabel">Tilt:</Typography>
                  <Typography variant="body2Regular">{row.tilt}</Typography>
                  {hasZoom && row.zoom && (
                    <>
                      <Typography variant="inputLabel">Zoom:</Typography>
                      <Typography variant="body2Regular">{row.zoom}</Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ p: "1em" }}>
            {[...Array(4)].map(
              (_): JSX.Element => (
                <Skeleton key={uuid()} variant="rectangular" sx={{ my: 3 }} />
              )
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PTZoneListTable;
